import { useReactFlow, Handle, Position, NodeResizer } from "@xyflow/react";
import { Button, Select, Input } from "antd";
import { OrderedListOutlined, CloseOutlined, PlusOutlined, DeleteOutlined, CopyOutlined } from "@ant-design/icons";
import NodeNameInput from "./NodeNameInput";

const CAPTURE_FIELDS = [
  { value: "firstname",   label: "First Name" },
  { value: "lastname",    label: "Last Name" },
  { value: "email",       label: "Email" },
  { value: "phone",       label: "Phone (secondary)" },
  { value: "company",     label: "Company" },
  { value: "designation", label: "Job Title" },
  { value: "notes",       label: "Notes (append)" },
  { value: "website",     label: "Website" },
  { value: "linkedin",    label: "LinkedIn" },
  { value: "instagram",   label: "Instagram" },
  { value: "telegram",    label: "Telegram" },
  { value: "twitter",     label: "Twitter" },
  { value: "facebook",    label: "Facebook" },
  { value: "attachment",  label: "Attachment" },
];

const COLOR = "#8b5cf6";

const QuestionnaireNode = ({ data, selected, id }) => {
  const { deleteElements } = useReactFlow();

  const questions = data.questions || [];

  const updateQuestion = (index, patch) => {
    const updated = questions.map((q, i) => i === index ? { ...q, ...patch } : q);
    data.onChange?.({ ...data, questions: updated });
  };

  const addQuestion = () => {
    const newQ = { id: `q_${Date.now()}`, text: "", field: "", rejectionMessage: "" };
    data.onChange?.({ ...data, questions: [...questions, newQ] });
  };

  const removeQuestion = (index) => {
    data.onChange?.({ ...data, questions: questions.filter((_, i) => i !== index) });
  };

  return (
    <div style={{
      background: "#fff",
      border: `2px solid ${selected ? COLOR : "#e5e7eb"}`,
      borderRadius: 10,
      padding: 14,
      minWidth: 300,
      maxWidth: 360,
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      width: "100%",
      height: "100%",
      boxSizing: "border-box",
    }}>
      <NodeResizer minWidth={240} minHeight={80} isVisible={selected} color={COLOR} />
      <Handle type="target" position={Position.Left} style={{ background: COLOR }} />

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <OrderedListOutlined style={{ color: COLOR, fontSize: 16 }} />
        <strong style={{ fontSize: 13 }}>Questionnaire</strong>
        <span style={{ marginLeft: 4, fontSize: 11, color: "#9ca3af" }}>({questions.length} questions)</span>
        <Button
          type="text" size="small" icon={<CopyOutlined />}
          onClick={() => data.onDuplicate?.()}
          style={{ marginLeft: "auto", color: "#9ca3af" }}
        />
        <Button
          type="text" size="small" icon={<CloseOutlined />}
          onClick={() => deleteElements({ nodes: [{ id }] })}
          style={{ color: "#9ca3af" }}
        />
      </div>

      <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <NodeNameInput data={data} />
        {questions.map((q, index) => (
          <div key={q.id || index} style={{
            border: "1px solid #e5e7eb",
            borderRadius: 7,
            padding: 10,
            marginBottom: 8,
            background: "#f9fafb",
          }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
              <span style={{
                width: 20, height: 20, borderRadius: "50%",
                background: COLOR, color: "#fff",
                fontSize: 10, fontWeight: 700,
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, marginRight: 6,
              }}>
                {index + 1}
              </span>
              <span style={{ fontSize: 11, color: "#6b7280", flex: 1 }}>Question {index + 1}</span>
              <Button
                type="text" size="small" icon={<DeleteOutlined />}
                onClick={() => removeQuestion(index)}
                style={{ color: "#ef4444", padding: "0 4px" }}
              />
            </div>

            <Input.TextArea
              value={q.text}
              onChange={(e) => updateQuestion(index, { text: e.target.value })}
              size="small"
              rows={2}
              placeholder="What do you want to ask?"
              style={{ marginBottom: 6 }}
            />

            <Select
              value={q.field || undefined}
              onChange={(v) => updateQuestion(index, { field: v })}
              size="small"
              style={{ width: "100%", marginBottom: 6 }}
              placeholder="Save reply as..."
              options={CAPTURE_FIELDS}
            />

            <Input
              value={q.rejectionMessage}
              onChange={(e) => updateQuestion(index, { rejectionMessage: e.target.value })}
              size="small"
              placeholder="Rejection message (optional)"
            />
          </div>
        ))}

        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          onClick={addQuestion}
          style={{ width: "100%", borderColor: COLOR, color: COLOR }}
        >
          Add Question
        </Button>
      </div>

      <div style={{
        marginTop: 10, padding: "5px 8px",
        background: `${COLOR}11`, borderRadius: 6,
        fontSize: 10, color: "#6d28d9",
      }}>
        Asks each question in order • max 3 retries per question • saves each reply to contact
      </div>

      <Handle type="source" position={Position.Right} style={{ background: COLOR }} />
    </div>
  );
};

export default QuestionnaireNode;
